import uuidv4 from "uuid/v4";
import lexiconManager from "../utils/lexicon";

/**
* NuCalBaseView.
* @abstract 
* @param {Object} props properties.
* @param {String} props.namespace The namespace in which this object should subscribe and publish events on.
* @param {HTMLElement} props.view The HTML Element in which this object should render the output too.
* @param {String} props.dateType Which date the object is dealing with. Can be "arrival", "departure" or "both". Default is "both".
* @param {String} props.locale Language code. Default "en-US."
* @param {String} props.UUID Unique Identifier. the default is randomly generated.
* @param {Object} props.lexicon The translation objects.
*/
class NuCalBaseView {

	constructor(props){
		try {
			this.view = props.view || null;
			if(this.view === null){
				throw new Error("view is not set");
			}
			this._subscriptions = [];
			this.dateType = props.dateType || "both";
			this.namespace = props.namespace || "";
			this.locale = props.locale || "en-US";
			this._lexicon = props.lexicon || lexiconManager.getLexicon(this.locale);
			this.UUID = uuidv4();
		}
		catch(e){
			console.log(e);
			return e;
		}
	}

	/**
    * Where the output of the object is rendered too.
    * @type {HTMLElement}
    */

	get view(){
		return this._view;
	}

	set view(v){
		this._view = v;
	}

	/**
    * The locale which to use with `toLocaleDateString` and such.
    * @type {String}
    */

	get locale(){
		return this._locale;
	}

	set locale(l){
		this._locale = l;
	}

	/**
	 * The translations in JSON
	 */

	get lexicon(){
		return this._lexicon;
	}

	set lexicon(l){
		this._lexicon = l;
		this.render();
	}

	/**
    * The namespace in which this object should subscribe and publish events on.
    * @type {String}
    */
	get namespace(){
		return this._namespace;
	}

	set namespace(n){
		this._namespace = n;
		this._subscriptions.forEach( sub => {
			sub.remove();
		});

		this._buildEvents();

		this.subscribe();
	}

	_buildEvents(){
		let n = this._namespace;
		if(typeof n == "string" && n !== ""){
			n = "/" + n;
		}else{
			n = "";
		}
		this._events = {};
		this._events.datesUpdated = `/nuCal${n}/datesUpdated`;
		this._events.arrivalDateUpdated = `/nuCal${n}/arrivalDateUpdated`;
		this._events.departureDateUpdated = `/nuCal${n}/departureDateUpdated`;
		this._events.minDateUpdated = `/nuCal${n}/minDateUpdated`;
		this._events.maxDateUpdated = `/nuCal${n}/maxDateUpdated`;
		this._events.updateArrivalDate = `/nuCal${n}/updateArrivalDate`;
		this._events.updateDepartureDate = `/nuCal${n}/updateDepartureDate`;
		this._events.updateNumberOfNights = `/nuCal${n}/updateNumberOfNights`;
		this._events.updateMinNumberOfNights = `/nuCal${n}/updateMinNumberOfNights`;
		this._events.updateMaxNumberOfNights = `/nuCal${n}/updateMaxNumberOfNights`;
	}

	/**
    * Which date the object is dealing with. Can be "arrival", "departure" or "both".
    * @type {String}
    * @default "both"
    */
	get dateType(){
		return this._dateType;
	}

	set dateType(type){
		if(["departure","arrival","both"].indexOf(type) !== -1){
			this._dateType = type;
		}else{
			console.log("not a valid dateType");
			this._dateType = "both";
		}
	}

	/**
    * The unique ID for the object.
    * @type {String}
    */
	get UUID(){
		return this._UUID;
	}

	set UUID(uuid){
		this._UUID = uuid || uuidv4();
	}

	/**
    * overload this to add subscriptions in child classes. This will allow incoming messages from other
    * published of classes to be recieved and processed. Typically the callback for the subscription will
    * be `this.render()`.
    * @type {Undefined}
    */
	subscribe(){
	}

	/**
    * overload this to generate and render the output and update `this.view`. This method is typically the
    * callback from a subscription added in `this.subcribe`. The `props` recieved are
    * sent from what ever is publishing. 
    * @type {Undefined}
    */
	render(){

	}

}

export default NuCalBaseView;